import {endpoint} from '~/api';
import {ID} from '~/lib/model';
import {
    IBusinessIncentive,
    IBusinessIncentiveCreateRequest,
    IBusinessIncentiveUpdateRequest,
    ITierConfig,
    IncentiveState,
    IncentiveStructure,
    IncentiveType,
    ValueUnit
} from '~/modules/BusinessIncentives/models/BusinessIncentive';
import {
    parseEnum,
    parseNullableString,
    parseNumber,
    parseString,
    unwrapValue,
} from '~/modules/BusinessIncentives/utils/value';

const parseScope = (input: unknown): IBusinessIncentive['scope'] => {
    const value = unwrapValue(input);
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'object') {
        return value as Record<string, unknown>;
    }
    return String(value);
};

const parseTierConfig = (input: unknown): ITierConfig => {
    const value = (input ?? {}) as Record<string, unknown>;
    return {
        name: parseString(value.name),
        thresholdUsd: parseNumber(value.thresholdUsd) ?? 0,
        percentage: parseNumber(value.percentage) ?? 0,
    };
};

const normalizeBusinessIncentive = (input: unknown): IBusinessIncentive => {
    const row = (input ?? {}) as Record<string, unknown>;
    const rawValueUnit = parseNullableString(row.valueUnit);
    return {
        id: parseNumber(row.id),
        incentiveType: parseEnum(row.incentiveType, Object.values(IncentiveType), IncentiveType.REBATE),
        structure: parseEnum(row.structure, Object.values(IncentiveStructure), IncentiveStructure.FIXED_FEE),
        scope: parseScope(row.scope),
        name: parseString(row.name),
        description: parseString(row.description),
        startDate: parseString(row.startDate),
        endDate: parseString(row.endDate),
        state: parseEnum(row.state, Object.values(IncentiveState), IncentiveState.INACTIVE),
        currentTier: parseNullableString(row.currentTier),
        tiers: Array.isArray(row.tiers) ? row.tiers.map(item => parseTierConfig(item)) : [],
        value: parseNumber(row.value),
        valueUnit: rawValueUnit && Object.values(ValueUnit).includes(rawValueUnit as ValueUnit)
            ? rawValueUnit as ValueUnit
            : null,
    };
};

export const fetchBusinessIncentives = () =>
    endpoint.get<unknown>('/expenses').then(r =>
        Array.isArray(r.data)
            ? r.data.map(item => normalizeBusinessIncentive(item))
            : []
    );

export const fetchBusinessIncentive = (id: ID) =>
    endpoint.get<unknown>(`/expenses/${id}`).then(r => normalizeBusinessIncentive(r.data));

export const createBusinessIncentive = (payload: IBusinessIncentiveCreateRequest) =>
    endpoint.post<unknown>('/expenses', payload).then(r => normalizeBusinessIncentive(r.data));

export const updateBusinessIncentive = (id: ID, payload: IBusinessIncentiveUpdateRequest) =>
    endpoint.put<unknown>(`/expenses/${id}`, payload).then(r => normalizeBusinessIncentive(r.data));

export const deleteBusinessIncentive = (id: ID) =>
    endpoint.delete(`/expenses/${id}`).then(() => undefined);
